import { useEffect, useRef, useState } from "react";
import type { TVideo } from "../../services/seasons/types";
import Player from "./Player";
import GiftModal from "../gift-modal/GiftModal";

interface MediaProps {
  video?: TVideo;
  onComplete?: (
    lessonId: number | null,
    options?: { auto?: boolean }
  ) => void;
  chapter_name?: string;
  freeAccess?: boolean;
}

const Media = ({ onComplete, video, chapter_name, freeAccess = false }: MediaProps) => {
  const [progress, setProgress] = useState<number>(0);
  const [completed, setCompleted] = useState<boolean>(false);
  const autoCompletedRef = useRef<boolean>(false);
  const [showGiftModal, setShowGiftModal] = useState<boolean>(false);

  useEffect(() => {
    // Initialize progress from persisted maxVideoViewSec (if available)
    if (video?.lessonId) {
      try {
        const raw = localStorage.getItem(`lesson_${video.lessonId}`);
        if (raw) {
          const parsed = JSON.parse(raw) as {
            maxVideoViewSec?: number;
            lastVideoViewSec?: number;
          } | null;
          const savedMax =
            typeof parsed?.maxVideoViewSec === "number"
              ? parsed!.maxVideoViewSec!
              : 0;
          const duration = typeof video.duration === "number" ? video.duration : 0;
          if (savedMax > 0 && duration > 0) {
            const pct = Math.max(0, Math.min(100, (savedMax / duration) * 100));
            setProgress(pct);
          } else {
            setProgress(0);
          }
        } else {
          setProgress(0);
        }
      } catch {
        setProgress(0);
      }
    } else {
      setProgress(0);
    }
    setCompleted(false);
    autoCompletedRef.current = false;
  }, [video]);

  useEffect(() => {
    if (progress >= 95 && !completed) {
      setCompleted(true);
    }

    // Check if this is lesson 25 and progress > 98
    if (video?.lessonId === 25 && progress > 98) {
      const hasShownGiftModal = localStorage.getItem("giftModalShown");
      if (!hasShownGiftModal) {
        setShowGiftModal(true);
        localStorage.setItem("giftModalShown", "true");
      }
    }
  }, [progress, completed, video]);

  useEffect(() => {
    if (progress >= 99 && video && !autoCompletedRef.current) {
      autoCompletedRef.current = true;
      // Auto complete reached 99%: delegate decision to parent via auto flag
      onComplete?.(video.lessonId, { auto: true });
      // Do not auto-select here; parent decides based on enrollment state
    }
  }, [progress, video, onComplete]);

  if (!video) return null;

  const handleGiftModalClose = () => {
    setShowGiftModal(false);
    if (video && (completed || freeAccess)) {
      onComplete?.(video.lessonId, { auto: false });
    }
  };

  return (
    <>
      {showGiftModal && <GiftModal onClose={handleGiftModalClose} />}
      <div className="bg-white/25 w-[366px] md:w-7/12 lg:w-5/12 h-52 md:h-72 lg:h-96 mx-auto mt-10 rounded-4xl p-2">
      <Player
        key={video?.id}
        src={video?.hlsUrl}
        poster={(() => {
          const OLD_DOMAIN = "s3.ir-thr-at1.arvanstorage.ir/vac-video-storage";
          const NEW_DOMAIN = "vod.vac.academy";
          const thumbnailUrl = video?.thumbnailUrl;
          if (thumbnailUrl && thumbnailUrl.includes(OLD_DOMAIN)) {
            return thumbnailUrl.replace(OLD_DOMAIN, NEW_DOMAIN);
          }
          return thumbnailUrl;
        })()}
        onProgress={(p) => setProgress(p)}
        lessonId={video?.lessonId}
      />
      <div className="flex mt-3 gap-2.5 justify-center items-center">
        <div className="flex items-center">
          <p className="text-lg md:text-xl font-kalameh font-medium">
            {chapter_name}
          </p>
        </div>
        <div
          className={`min-w-28 h-8 flex items-center justify-center bg-[#CAD1D4] rounded-4xl relative overflow-hidden cursor-pointer ${
            completed || freeAccess ? "opacity-100" : "opacity-90"
          }`}
          onClick={() => {
            if (video && (completed || freeAccess)) {
              onComplete?.(video.lessonId, { auto: false });
            }
          }}
        >
          <div
            className={`absolute right-0 h-full ${
              completed || freeAccess ? "bg-[#58C2F9]" : "bg-[#32C3A3]"
            } rounded-4xl`}
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
          <p className="text-sm font-kalameh text-white z-10">
            {completed || freeAccess ? "درس بعدی" : "تکمیل درس"}
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default Media;
